/**
 * Custom React hook for managing feature selection in a dropdown UI.
 *
 * - Accepts:
 *   - rawFeatureData: array of feature objects to display as options.
 *   - selectedFeatures: array of currently selected feature names.
 *   - setSelectedFeatures: setter function to update selected features.
 * - Builds dropdown options with "Select All" and a divider, followed by features (using index-based keys).
 * - Computes selected dropdown keys, including "Select All" if all features are selected.
 * - Handles selection logic:
 *   - "Select All" toggles all features on/off.
 *   - Individual feature selection toggles that feature in the selection.
 * - Returns:
 *   - featureOptions: array of dropdown options for rendering.
 *   - selectedKeys: array of selected option keys for the dropdown.
 *   - handleFeatureSelection: event handler for dropdown selection changes.
 */
import { IDropdownOption, SelectableOptionMenuItemType } from "@fluentui/react";
import { FormEvent, useCallback, useMemo } from "react";
import { FeatureData } from "../ExploreFeatures.interfaces";
import { DIVIDER_KEY, SELECT_ALL_KEY } from "../../../components/CustomerView/CustomerView.constants";
import { logEvent } from "../../../helper/AppInsightsHelper";

export const useFeatureSelection = (
  rawFeatureData: FeatureData[],
  selectedFeatures: string[],
  setSelectedFeatures: (features: string[]) => void,
  customProps: {
    pageName: string;
    tabName: string;
    apiName: string;
  }
) => {
  // Memoize feature options to avoid unnecessary recalculations
  const featureOptions: IDropdownOption[] = useMemo(() => {
    const options: IDropdownOption[] = [
      { key: SELECT_ALL_KEY, text: "Select All" },
      { key: DIVIDER_KEY, text: "-", itemType: SelectableOptionMenuItemType.Divider }
    ];
    // Use index-based keys to avoid collisions and handle duplicate names
    options.push(
      ...rawFeatureData.map((feature, index) => ({
        key: `feature-${index}`,
        text: feature.name
      }))
    );
    return options;
  }, [rawFeatureData]);

  // Map feature names to dropdown keys for selection
  const featureNameToKey = useMemo(() => {
    const map = new Map<string, string>();
    rawFeatureData.forEach((feature, index) => {
      map.set(feature.name, `feature-${index}`);
    });
    return map;
  }, [rawFeatureData]);

  const selectedKeys = useMemo(() => {
    const featureKeys = selectedFeatures.map((name) => featureNameToKey.get(name)).filter(Boolean) as string[];
    const isAllSelected = featureKeys.length === rawFeatureData.length && rawFeatureData.length > 0;
    return isAllSelected ? [...featureKeys, SELECT_ALL_KEY] : featureKeys;
  }, [selectedFeatures, rawFeatureData, featureNameToKey]);

  const handleFeatureSelection = useCallback(
    (_event: FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
      if (!option || option.key === DIVIDER_KEY) return;

      if (option.key === SELECT_ALL_KEY) {
        const isAllSelected = selectedFeatures.length === rawFeatureData.length;
        logEvent(customProps.tabName, customProps.pageName, "ExploreFeatures_" + customProps.apiName, {
          eventName: "FeatureSelection_SelectAllToggled",
          hookName: "useFeatureSelection",
          action: isAllSelected ? "deselect_all" : "select_all",
          totalFeatures: rawFeatureData.length
        });
        setSelectedFeatures(isAllSelected ? [] : rawFeatureData.map((f) => f.name));
      } else {
        // Find the feature name by key
        const index =
          typeof option.key === "string" && option.key.startsWith("feature-")
            ? parseInt(option.key.replace("feature-", ""), 10)
            : -1;
        const featureName = rawFeatureData[index]?.name;
        if (!featureName) {
          logEvent(customProps.tabName, customProps.pageName, "ExploreFeatures_" + customProps.apiName, {
            eventName: "FeatureSelection_InvalidFeatureKey",
            hookName: "useFeatureSelection",
            key: option.key
          });
          return;
        }
        const isSelected = selectedFeatures.includes(featureName);
        logEvent(customProps.tabName, customProps.pageName, "ExploreFeatures_" + customProps.apiName, {
          eventName: "FeatureSelection_FeatureToggled",
          hookName: "useFeatureSelection",
          featureName,
          action: isSelected ? "deselect" : "select"
        });
        setSelectedFeatures(
          isSelected ? selectedFeatures.filter((name) => name !== featureName) : [...selectedFeatures, featureName]
        );
      }
    },
    [selectedFeatures, rawFeatureData, setSelectedFeatures, customProps]
  );

  return {
    featureOptions,
    selectedKeys,
    handleFeatureSelection
  };
};
